/**
 * Product personalisation: radio toggle, validation, and multi-item add-to-cart.
 */
(function () {
  const PERSONALISATION_FIELD_IDS = [
    "personalised-shirt-name",
    "personalised-shirt-number",
    "personalised-shirt-player",
    "personalised-name-number",
    "personalised-name",
    "personalised-message",
    "personalised-file"
  ];

  function hasPersonalisationFields() {
    return PERSONALISATION_FIELD_IDS.some((id) => document.getElementById(id));
  }

  function getErrorEl(productForm) {
    const scope = productForm?.closest(".product") || document;
    return scope.querySelector(".personalisation-error-msg");
  }

  function showError(productForm, message) {
    const errorEl = getErrorEl(productForm);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      errorEl.style.display = "block";
    } else {
      window.alert(message);
    }
  }

  function clearError(productForm) {
    const errorEl = getErrorEl(productForm);
    if (!errorEl) return;
    errorEl.textContent = "";
    errorEl.hidden = true;
    errorEl.style.display = "none";
  }

  function isShirtPersonalised() {
    return Array.from(document.querySelectorAll(".radio-group .radio-group-input")).some(
      (radio) => radio.checked && radio.value === "Yes"
    );
  }

  function validatePersonalisation(productForm) {
    clearError(productForm);

    const shirtName = document.getElementById("personalised-shirt-name");
    const shirtNumber = document.getElementById("personalised-shirt-number");
    const shirtPlayer = document.getElementById("personalised-shirt-player");
    const nameNumber = document.getElementById("personalised-name-number");
    const name = document.getElementById("personalised-name");

    if (isShirtPersonalised()) {
      if (shirtPlayer) {
        if (!shirtPlayer.value.trim()) {
          showError(productForm, "Please select a player name & number to personalise this shirt.");
          return false;
        }
      } else if (!shirtName?.value?.trim() || !shirtNumber?.value?.trim()) {
        showError(productForm, "Please enter both name and number to personalise this shirt.");
        return false;
      }
    }

    if (nameNumber && !nameNumber.value.trim()) {
      showError(productForm, "Please enter a name & number to personalise this item.");
      return false;
    }

    if (name && !shirtName && !name.value.trim()) {
      showError(productForm, "Please enter a name to personalise this item.");
      return false;
    }

    return true;
  }

  function collectProperties() {
    const shirtName = document.getElementById("personalised-shirt-name");
    const shirtNumber = document.getElementById("personalised-shirt-number");
    const shirtPlayer = document.getElementById("personalised-shirt-player");
    const nameNumber = document.getElementById("personalised-name-number");
    const name = document.getElementById("personalised-name");
    const scoreboardMessage = document.getElementById("personalised-message");

    const isPersonalisedShirtSelect = !!shirtPlayer?.value?.trim();
    const isPersonalisedShirt =
      isPersonalisedShirtSelect || (!!shirtName?.value?.trim() && !!shirtNumber?.value?.trim());
    const isPersonalisedNameNumber = !!nameNumber?.value?.trim();
    const isPersonalisedName = !!name?.value?.trim();

    let properties = {};
    if (isPersonalisedShirtSelect) {
      properties = { Player: shirtPlayer.value.trim() };
    } else if (isPersonalisedShirt) {
      properties = {
        Name: shirtName.value.trim(),
        Number: shirtNumber.value.trim()
      };
    } else if (isPersonalisedNameNumber) {
      properties = { "Name & Number": nameNumber.value.trim() };
    } else if (isPersonalisedName) {
      properties = { Name: name.value.trim() };
    }

    if (scoreboardMessage?.value?.trim()) {
      properties.Message = scoreboardMessage.value.trim();
    }

    return {
      properties,
      isPersonalisedShirt
    };
  }

  function setLoading(productForm, loading) {
    const submitButton = productForm.querySelector('[type="submit"]');
    const spinner = productForm.querySelector(".loading__spinner");
    if (!submitButton) return;

    if (loading) {
      submitButton.setAttribute("aria-disabled", "true");
      submitButton.classList.add("loading");
      spinner?.classList.remove("hidden");
    } else {
      submitButton.classList.remove("loading");
      if (!productForm.error) submitButton.removeAttribute("aria-disabled");
      spinner?.classList.add("hidden");
    }
  }

  function handleErrorMessage(productForm, errorMessage) {
    if (productForm.dataset.hideErrors === "true") return;

    const wrapper =
      productForm.querySelector(".product-form__error-message-wrapper");
    if (!wrapper) return;
    const messageEl = wrapper.querySelector(".product-form__error-message");
    wrapper.toggleAttribute("hidden", !errorMessage);
    if (errorMessage && messageEl) messageEl.textContent = errorMessage;
  }

  function buildCartFormData(productForm, form) {
    const variantId = form.querySelector(".product-variant-id")?.value;
    const quantityInput = form.querySelector("[name='quantity']");
    let quantity = parseInt(quantityInput?.value, 10);
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;

    const { properties, isPersonalisedShirt } = collectProperties();
    const fd = new FormData();

    fd.append("items[0][id]", variantId);
    fd.append("items[0][quantity]", String(quantity));

    const sellingPlan = form.querySelector("[name='selling_plan']")?.value;
    if (sellingPlan) fd.append("items[0][selling_plan]", sellingPlan);

    if (productForm.hasAttribute("data-has-pre-order")) {
      fd.append("items[0][properties][Pre-order]", "✓");
    }

    Object.entries(properties).forEach(([key, value]) => {
      fd.append(`items[0][properties][${key}]`, value);
    });

    const scoreboardFile = document.getElementById("personalised-file");
    if (scoreboardFile?.files?.[0]) {
      fd.append(
        "items[0][properties][Image]",
        scoreboardFile.files[0],
        scoreboardFile.files[0].name
      );
    }

    if (isPersonalisedShirt) {
      const personalisedProductId = productForm.dataset.personaliseProductId;
      if (!personalisedProductId) {
        throw new Error("Personalised product ID is not found");
      }
      fd.append("items[1][id]", personalisedProductId);
      fd.append("items[1][quantity]", String(quantity));
    }

    const cart =
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
    if (cart) {
      fd.append(
        "sections",
        cart.getSectionsToRender().map((section) => section.section)
      );
      fd.append("sections_url", window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    return { fd, variantId, cart };
  }

  async function submitPersonalisation(productForm, form) {
    productForm.error = false;
    handleErrorMessage(productForm, false);
    setLoading(productForm, true);

    try {
      const { fd, variantId, cart } = buildCartFormData(productForm, form);
      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = fd;

      const response = await fetch(`${routes.cart_add_url}`, config).then((res) =>
        res.json()
      );

      if (response.status) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: "product-form",
          productVariantId: variantId,
          errors: response.errors || response.description,
          message: response.message
        });
        handleErrorMessage(productForm, response.description);
        showError(productForm, response.description || response.message || "Unable to add to cart");
        productForm.error = true;
        return;
      }

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "product-form",
        productVariantId: variantId,
        cartData: response
      });
      cart.renderContents(response);
    } catch (error) {
      console.error("Failed to add personalised item to cart", error);
      showError(productForm, error.message || "Failed to add to cart");
      productForm.error = true;
    } finally {
      setLoading(productForm, false);
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer?.classList.contains("is-empty")) {
        cartDrawer.classList.remove("is-empty");
      }
    }
  }

  function onSubmitCapture(event) {
    const form = event.target;
    const productForm = form.closest("product-form");
    if (!productForm?.hasAttribute("data-has-personalisation")) return;
    if (!hasPersonalisationFields()) return;

    if (productForm.querySelector('[type="submit"]')?.getAttribute("aria-disabled") === "true") {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (!validatePersonalisation(productForm)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    submitPersonalisation(productForm, form);
  }

  function handleRadioChange(radio) {
    const wrapper = radio.closest(".radio-group");
    if (!wrapper) return;
    const targetSelector = wrapper.getAttribute("data-target");
    if (!targetSelector) return;

    const targets = document.querySelectorAll(targetSelector);
    const shirtInputs = document.getElementsByClassName("shirt-input");

    if (radio.checked && radio.value === "Yes") {
      targets.forEach((target) => target.classList.remove("shirt-name-number"));
      Array.from(shirtInputs).forEach((input) => {
        input.disabled = false;
      });
      return;
    }

    if (radio.checked && radio.value !== "Yes") {
      Array.from(shirtInputs).forEach((input) => {
        input.value = "";
        input.disabled = true;
      });
      targets.forEach((target) => target.classList.add("shirt-name-number"));
    }
  }

  function initRadioToggle() {
    const radios = document.querySelectorAll(".radio-group .radio-group-input");
    radios.forEach((radio) => {
      radio.addEventListener("change", (event) => handleRadioChange(event.target));
      if (radio.checked) handleRadioChange(radio);
    });
  }

  function init() {
    initRadioToggle();
    document.querySelectorAll("product-form[data-has-personalisation] form").forEach((form) => {
      form.addEventListener("submit", onSubmitCapture, true);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
